import * as React from "react";
import { Button, Text } from "@uikit";
import Textbox from "@uikit/textbox";
import { IoCalendarOutline } from "@react-icons/all-files/io5/IoCalendarOutline";
import { MoonIcon, UsersIcon } from "@heroicons/react/solid";
import { IoBed } from "@react-icons/all-files/io5/IoBed";
import dayjs from "dayjs";
import { useApp } from "@store/app";
import { Hotel_Package } from "@kalabo/golfsavers-common";
import DateField from "@uikit/datefield";

interface IProps {
  packages?: Hotel_Package[];
  showRoomType?: boolean;
}

const SearchParams = ({ packages, showRoomType = true }: IProps) => {
  const setNights = useApp(($) => $.setNights);
  const setRoom = useApp(($) => $.setRoomType);
  const setPackageDate = useApp(($) => $.setPackageDate);
  const package_date = useApp(($) => $.package_date);
  const state_number_of_nights = useApp(($) => $.number_of_nights);
  const state_room_type = useApp(($) => $.room_type);

  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [arrival_date, setArrivalDate] = React.useState<string | null>(
    package_date
  );

  const [number_of_nights, setNumberOfNights] = React.useState<number>(
    state_number_of_nights
  );

  const [room_type, setRoomType] = React.useState<string | null>(
    state_room_type
  );

  const [showDatePicker, setShowDatePicker] = React.useState<boolean>(false);

  // get a list of the room types from the packages array
  const room_types = [...new Set(packages?.map((p) => p.room.room_type))];

  const nights = [...new Set(packages?.map((p) => p.number_of_nights))];

  let night_options = React.useMemo(() => {
    let n = [];
    [...Array(7)].map((_, i) => {
      n.push(i + 1);
    });
    n.push(10);
    n.push(14);

    // filter out any night that are not in nights
    n = n.filter((n) => nights.includes(n));

    return n;
  }, [nights]);

  const isButtonDisabled = React.useMemo(() => {
    let disabled = false;

    if (!isDirty) return true;

    if (showRoomType) {
      if (isDirty && !arrival_date && !number_of_nights && !room_type) {
        disabled = false;
      } else if (!room_type && !arrival_date && !number_of_nights) {
        disabled = true;
      }
    } else {
      if (isDirty && !arrival_date && !number_of_nights) {
        disabled = false;
      } else if (!arrival_date && !number_of_nights) {
        disabled = true;
      }
    }
    return disabled;
  }, [isDirty, arrival_date, number_of_nights, room_type, showRoomType]);

  const showWarning = React.useMemo(() => {
    let show = false;
    if (!isDirty && !arrival_date && !number_of_nights && !room_type) {
      show = true;
    }

    return show;
  }, [isDirty, arrival_date, number_of_nights, room_type, showRoomType]);

  return (
    <>
      <div
        style={{
          boxShadow: "0px 5px 14px 0px rgba(0, 0, 0, 0.10)",
        }}
        className="p-5 my-8 bg-white rounded-[10px] border border-grey3"
      >
        {showWarning && (
          <Text
            size={13}
            weight="semibold"
            leading={1.3}
            color="darkorange"
            className="mb-3"
          >
            Select your arrival date and the number of nights to see available
            packages and rates:
          </Text>
        )}
        <div className="grid grid-cols-12">
          <div className="col-span-12">
            <div className="mt-1 flex flex-col lg:flex-row flex-1 gap-4">
              <div className="flex flex-1 flex-col md:flex-row gap-4">
                <div className="flex flex-1">
                  <DateField
                    id="arrival_date"
                    label="Arrival date"
                    format="ddd, DD MMM YYYY"
                    size={"large"}
                    icon={
                      <IoCalendarOutline className="w-6 h-6 text-grey7  " />
                    }
                    className={`${!arrival_date && "text-black/60"}`}
                    onChange={(e) => {
                      setIsDirty(true);
                      setArrivalDate(e);
                    }}
                    value={arrival_date ? dayjs(arrival_date).toDate() : null}
                  />
                </div>
                {showRoomType && room_types.length > 1 && (
                  <div className="flex flex-1">
                    <div className="flex items-center relative flex-1 py-2 md:py-0 px-4 border border-grey3 rounded-[6px] overflow-hidden">
                      <span className="absolute top-[7px] left-[52px] text-12 font-semibold text-black/80">
                        Room type
                      </span>
                      <IoBed className="w-6 h-6 text-grey7  " />
                      <div className="absolute right-0 bg-white  w-12 h-full"></div>
                      <select
                        className={`!w-full ml-[1px] mt-[15px] mb-[3px] !py-0 border-none text-15 focus:ring-0 !appearance-none ${
                          !room_type ? "text-[#22272999]" : ""
                        }`}
                        onChange={(e) => {
                          setIsDirty(true);
                          if (e.target.value === "") {
                            setRoomType(null);
                          } else {
                            setRoomType(e.target.value);
                          }
                        }}
                        value={room_type ? String(room_type) : ""}
                      >
                        <option value={""}>Any</option>
                        {room_types.map((_, i) => (
                          <option key={i} value={room_types[i]}>
                            {room_types[i]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="flex flex-1">
                  <div className="flex items-center relative flex-1 py-2 md:py-0 px-4 border border-grey3 rounded-[6px] overflow-hidden">
                    <span className="absolute top-[7px] left-[52px] text-12 font-semibold text-black/80">
                      No. of nights
                    </span>
                    <MoonIcon className="w-6 h-6 text-grey7  " />
                    <div className="absolute right-0 bg-white  w-12 h-full"></div>
                    <select
                      className={`!w-full ml-[1px] mt-[15px] mb-[3px] !py-0 border-none text-16 focus:ring-0 !appearance-none ${
                        !number_of_nights ? "text-[#22272999]" : ""
                      }`}
                      onChange={(e) => {
                        setIsDirty(true);
                        if (e.target.value === "") {
                          setNumberOfNights(null);
                        } else {
                          setNumberOfNights(Number(e.target.value));
                        }
                      }}
                      value={number_of_nights ? String(number_of_nights) : ""}
                    >
                      <option value={""}>Any</option>
                      {night_options.map((n, i) => (
                        <option key={n} value={n}>
                          {n} night{n > 1 && "s"}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex">
                <Button
                  onClick={async () => {
                    setIsDirty(false);
                    setNights(number_of_nights);
                    setRoom(room_type);
                    setPackageDate(arrival_date);
                  }}
                  disabled={isButtonDisabled}
                  size="large"
                  color="blue"
                  text={"Show Prices"}
                  className="!m-0 !w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchParams;
